import { Button, Grid } from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";
import { isEmpty, Loading, Show, SimpleShowLayout, useDataProvider, useNotify, useRecordContext, useRefresh } from 'react-admin';
import ListActions from '../common/ListActions';
import { ActionTitles, RESOURCES, RESOURCES_BASEPATH } from '../Constants';
import { baseButtonStyle, contentCenter, padding0, padding2, textColor } from '../Styles';
import { Error404 } from "../errors/Error404";
import { useEffect, useState } from "react";
import { BooleanFieldShowComponent } from "../common/BooleanComponents";
import CustomDialog from "../../utils/CustomDialog";
import JsonView from "../common/JsonView";

export const ReportThemeShow = () => (
  <Show
    actions={
      <ListActions
        title={ActionTitles.REPORT_THEME_SHOW}
        showEditButton={true}
        showDeleteButton={true}
        basePath={RESOURCES_BASEPATH.REPORT_THEME}
        resource={RESOURCES.REPORT_THEME}
        recordName={ActionTitles.REPORT_THEMES}
      />
    }
    sx={contentCenter}
  >
    <SimpleShowLayout>
      <ReportThemeShowGrid />
    </SimpleShowLayout>
  </Show>
);

const ReportThemeShowGrid = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [clientChoices, setClientChoices] = useState();
  const [reportThemeChoices, setReportThemeChoices] = useState([]);
  const [reportTemplates, setReportTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [defaultDialogOpen, setDefaultDialogOpen] = useState(false);

  const handleDefaultDialogClose = () => {
    setDefaultDialogOpen(false);
  };

  const confirmSetDefault = () => {
    dataProvider
      .setDefaultReportTheme({ id: record.id })
      .then(() => {
        notify("Marked as default", { type: "info", autoHideDuration: 1000 });
        handleDefaultDialogClose();
        refresh();
      })
      .catch((error) => {
        notify(error?.message ?? "Unable to mark as default", { type: "warning", autoHideDuration: 2000 });
        handleDefaultDialogClose();
      });
  };

  useEffect(() => {
    Promise.all([
      dataProvider.getClientNameList("admin/client/names"),
      dataProvider.getAllReportThemes(),
      dataProvider.getAllReportThemeTemplates(),
    ])
      .then(([clientData, reportTheme, templates]) => {
        setClientChoices(clientData.data);
        setReportThemeChoices(reportTheme.data);
        setReportTemplates(templates.data);
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  if (loading) return <Loading />;
  if (error) return <Error404 />;

  return (
    <Grid container flexGrow={1}>
      <Grid xs={12}>
        <List sx={padding0.y}>
          <ListComponentForReportTheme title="Client" value={clientChoices.find(item => item.id === record["clientId"])?.name} />
          <Divider light />
          <ListComponentForReportTheme title="Report Theme" value={reportThemeChoices.find(item => item.id === record["reportThemeId"])?.name} />
          <Divider light />
          {!isEmpty(record["reportThemeTemplateId"]) && (
            <>
              <ListComponentForReportTheme title="Report Template" value={reportTemplates.find(item => item.id === record["reportThemeTemplateId"])?.name} />
              <Divider light />
            </>
          )}
          <BooleanFieldShowComponent
            name="default"
            title="Default"
            record={record}
          />
          {!isEmpty(record.configuration) && Object.entries(JSON.parse(record.configuration)).map(([key, value]) => (
            <>
              {<ListComponentForReportTheme title={key} value={typeof value === 'object' ? <JsonView value={value} /> : String(value)} />}
              <Divider light />
            </>
          ))}
          <ListComponentForReportTheme title="Created" value={record["createdAt"]} />          
          <Divider light />
          <ListComponentForReportTheme title="Comment" value={record["versionComment"] ?? "--"} />
        </List>
        {!record.default && (
          <Grid container justifyContent="flex-end">
            <Button
              id="report-theme-show-set-default-button"
              variant="contained"
              size="small"
              sx={{ ...baseButtonStyle(), width: "fit-content" }}
              onClick={() => setDefaultDialogOpen(true)}
            >
              Set as Default
            </Button>
          </Grid>
        )}
      </Grid>
      <CustomDialog
        openDialog={defaultDialogOpen}
        closeDialog={handleDefaultDialogClose}
        title="Set as Default"
        content={<p>Are you sure you want to mark this {ActionTitles.REPORT_THEMES} as default?</p>}
        handleSubmit={confirmSetDefault}
        submitBtnTitle="Confirm"
        hideDivider={true}
      />
    </Grid>
  )
};

const ListComponentForReportTheme = (props) => {
  return (
    <ListItem sx={padding2.y}>
      <Grid container>
        <Grid item xs={12} md={4} sx={textColor.gray}>{props.title}</Grid>
        <Grid item xs={12} md={8}>{props.value}</Grid>
      </Grid>
    </ListItem>
  );
};